import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import InvertColorsOffIcon from "@mui/icons-material/InvertColorsOff";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Icon from "common/components/Elements/Icon/Icon";
import ResizerHandle, { resizerHandleWidth } from "common/components/Elements/ResizerHandle/ResizerHandle";
import type { ToggleButtonOption } from "common/components/Elements/ToggleButton/ToggleButton";
import ToggleButton from "common/components/Elements/ToggleButton/ToggleButton";
import { viewportWidth } from "common/utilities/browser/viewport/viewport";
import useWindowResize from "common/utilities/hooks/useWindowResize/useWindowResize";
import DropDown from "core/components/Elements/DropDown/DropDown";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import { sx, ThemeColors, ThemeVars } from "core/utilities/theme";
import I18n from "i18n/en-US";
import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import type { SequenceAlign, SequenceType } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import Search from "sequenceAnalyser/components/Search/Search";
import { SHOW_PRINT_VIEW } from "sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";
import SequenceTableRuler from "sequenceAnalyser/components/Sequences/SequenceTableRuler/SequenceTableRuler";
import EditSelectionRangeModal from "sequenceAnalyser/components/TableActions/EditSelectionRangeModal/EditSelectionRangeModal";
import {
  clearSelection,
  createSelection,
  updateSidebarPixelWidth,
} from "sequenceAnalyser/redux/actions/sequenceAnalyserActions";
import type { SequencesSearch, SequencesSearchScope } from "sequenceAnalyser/utilities/search/search";
import {
  markResiduesFromSelectionAsSelected,
  type SequenceSelection,
  toggleSelectionVisibility,
} from "sequenceAnalyser/utilities/selection/selection";
import { sequenceSelectionAsString } from "sequenceAnalyser/utilities/selectionActions/selectionActions";
import type { SequenceAnalyserTableView } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { SequenceAnalyserTableViews } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { longestSequence, sequenceAlignLength } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

import classes from "./TableActionsHeader.style";

const isContinuousSelection = (selection: SequenceSelection, alignedSequences: SequenceAlign[]) => {
  const indexes = selection.ids.map((id) => alignedSequences.findIndex((s) => s.id === id)).sort((a, b) => a - b);

  const notContinuous = indexes.some((index, i) =>
    indexes[i + 1] === undefined ? false : index + 1 !== indexes[i + 1],
  );

  return !notContinuous;
};

const minSidebarWidth = 100;
export const maxSidebarWidth = () => viewportWidth() - minSidebarWidth;

export const viewOptions = (
  maskingResidue: string,
  active?: SequenceAnalyserTableView,
): ToggleButtonOption<SequenceAnalyserTableViews>[] => [
  {
    id: SequenceAnalyserTableViews.default,
    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.default,
    content:
      active === SequenceAnalyserTableViews.default
        ? "A"
        : I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.default,
  },
  {
    id: SequenceAnalyserTableViews.abbreviated,
    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.abbreviated,
    content:
      active === SequenceAnalyserTableViews.abbreviated
        ? maskingResidue
        : I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.abbreviated,
  },
  {
    id: SequenceAnalyserTableViews.abbreviatedWithoutColors,
    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.abbreviatedWithoutColors,
    content:
      active === SequenceAnalyserTableViews.abbreviatedWithoutColors ? (
        <>
          <Box>
            <span>{maskingResidue}</span>
          </Box>
          <Box height={18} mt={-0.5}>
            <InvertColorsOffIcon />
          </Box>
        </>
      ) : (
        I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.abbreviatedWithoutColors
      ),
  },
  {
    id: SequenceAnalyserTableViews.abbreviatedWithoutColorsAndFeaturesColors,
    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.abbreviatedWithoutColorsAndFeaturesColors,
    content:
      active === SequenceAnalyserTableViews.abbreviatedWithoutColorsAndFeaturesColors ? (
        <>
          <Box>
            <span>{maskingResidue}</span>
          </Box>
          <Box height={18} mt={-0.5}>
            <FormatColorResetIcon />
          </Box>
        </>
      ) : (
        I18n.SequenceAnalyser.TableActions.TableActionsHeader.viewOptions.abbreviatedWithoutColorsAndFeaturesColors
      ),
  },
];

interface PropsFromState {
  zoomLevel: number;
}

interface ComponentProps extends PropsFromState {
  view: SequenceAnalyserTableView;
  onViewChange: (view: SequenceAnalyserTableView) => void;
  alignedSequences: SequenceAlign[];
  onSearchChange: (query: string, scope: SequencesSearchScope, selection: SequenceSelection) => void;
  search: SequencesSearch;
  sessionSequenceType: SequenceType;
  maskingResidue: string;
}

function TableActionsHeader({
  zoomLevel,
  search,
  onSearchChange,
  sessionSequenceType,
  onViewChange,
  view,
  alignedSequences,
  maskingResidue,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();

  const showAppHeaderEnv = useSelector((state: ApplicationState) => state.ui.showAppHeaderEnv);
  const selection = useSelector((state: ApplicationState) => state.sequenceAnalyser.selection);
  const sidebarPixelWidth = useSelector((state: ApplicationState) => state.sequenceAnalyser.sidebarPixelWidth);

  const [dragging, setDragging] = useState(false);
  const [openEditSelectionRangeModal, setOpenEditSelectionRangeModal] = useState(false);

  const selectedViewOption = viewOptions(maskingResidue, view).find((o) => o.id === view);
  const [hoverSelection, setHoverSelection] = React.useState(false);
  const zoomLevelPercentage = (zoomLevel || 100) / 100;
  const sidebarRatioWidth = sidebarPixelWidth * zoomLevelPercentage;

  const handleResize = (cursorPositionX: number) => {
    dispatch(updateSidebarPixelWidth(cursorPositionX + resizerHandleWidth / 2));
  };

  useWindowResize(() => {
    // Ensure the sidebar is resized so fit within the screen when the user resize its browser
    const cursorPositionX = sidebarPixelWidth > maxSidebarWidth() ? maxSidebarWidth() : sidebarPixelWidth;
    handleResize(cursorPositionX - resizerHandleWidth);
  });

  useEffect(() => {
    // Only apply selection when not dragging and when selection changes
    if (!dragging && selection) {
      markResiduesFromSelectionAsSelected(selection);
    }
  }, [dragging, selection]);

  useEffect(() => {
    // Toggle visibility instead of recreating
    toggleSelectionVisibility(!dragging);
  }, [dragging]);

  useEffect(() => {
    setDragging(false);
  }, [sidebarPixelWidth]);

  const canSearch =
    selection.ids.length === 1 && selection.positionStart !== undefined && selection.positionEnd !== undefined;

  let top = ThemeVars.headerMenuHeight;
  if (showAppHeaderEnv) top += ThemeVars.headerEnvHeight;

  return (
    <Box display="block" displayPrint="none">
      <Box position="fixed" sx={classes.actionBar} top={top}>
        <Box position="absolute" top="5px" title={I18n.SequenceAnalyser.TableActions.resize}>
          <ResizerHandle
            // Shift by half the width of the icon to be aligned with the line separator
            left={sidebarRatioWidth - resizerHandleWidth / 2}
            onResize={handleResize}
            onStart={() => setDragging(true)}
            show={hoverSelection}
            maxLeft={maxSidebarWidth()}
            minLeft={minSidebarWidth}
            zoomLevel={zoomLevel}
          />
        </Box>
        <Box display="flex" onMouseEnter={() => setHoverSelection(true)} onMouseLeave={() => setHoverSelection(false)}>
          <Box
            position="relative"
            sx={sx(classes.actions, selection.ids.length > 0 ? classes.actionsWithSelection : undefined, {
              display: "flex",
              width: `${sidebarRatioWidth}px`,
            })}
          >
            <Box
              sx={{
                flexGrow: 1,
                "& button": {
                  backgroundColor: selection.ids.length > 0 ? ThemeColors.sequenceAnalyserSelection : undefined,
                  width: "100%",
                },
              }}
            >
              <DropDown
                id="selectionStatus"
                action="tertiary"
                label={
                  selection.ids.length > 0
                    ? I18n.SequenceAnalyser.TableActions.TableActionsHeader.selectionSummary(selection)
                    : I18n.SequenceAnalyser.TableActions.TableActionsHeader.sequences(alignedSequences.length)
                }
                items={[
                  {
                    id: "selectAll",
                    label: I18n.SequenceAnalyser.TableActions.TableActionsHeader.selectAll,
                    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.selectAllTitle,
                    active: true,
                    commands: [],
                    onClick: () => {
                      dispatch(clearSelection());

                      dispatch(
                        createSelection({
                          ids: alignedSequences.map((s) => s.id),
                          positionStart: undefined,
                          positionEnd: undefined,
                        }),
                      );
                    },
                  },
                  {
                    id: "invert",
                    label: I18n.SequenceAnalyser.TableActions.TableActionsHeader.invert,
                    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.invertTitle,
                    active: true,
                    commands: [],
                    onClick: () => {
                      dispatch(clearSelection());

                      const newSelectionIds = alignedSequences
                        .map((s) => s.id)
                        .filter((id) => !selection.ids.includes(id));

                      dispatch(
                        createSelection({
                          ids: newSelectionIds,
                          positionStart: undefined,
                          positionEnd: undefined,
                        }),
                      );
                    },
                  },
                  {
                    id: "editRange",
                    label: I18n.SequenceAnalyser.TableActions.TableActionsHeader.editRange,
                    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.editRangeTitle,
                    active:
                      selection.ids.length === 1 ||
                      (selection.ids.length > 1 && isContinuousSelection(selection, alignedSequences)),
                    commands: [],
                    onClick: () => setOpenEditSelectionRangeModal(true),
                  },
                  {
                    id: "clear",
                    label: I18n.SequenceAnalyser.TableActions.TableActionsHeader.clear,
                    title: I18n.SequenceAnalyser.TableActions.TableActionsHeader.clearTitle,
                    active: selection.ids.length > 0,
                    commands: [],
                    onClick: () => dispatch(clearSelection()),
                  },
                ]}
              />
            </Box>

            {canSearch && (
              <IconButton
                onClick={() => {
                  onSearchChange(sequenceSelectionAsString(alignedSequences, selection), search.scope, selection);
                }}
                size="small"
                style={{ padding: 0 }}
                title={I18n.SequenceAnalyser.TableActions.TableActionsHeader.searchRange}
              >
                <Icon element={SearchIcon} />
              </IconButton>
            )}
          </Box>
          <Box display="flex" overflow="hidden" whiteSpace="nowrap" width={`calc(100% - ${sidebarRatioWidth}px)`}>
            <Box display="inline-block" flexGrow="1" pt={0.5} pb={0.5} />
            <Box display="inline-block" mr={2} mt={0.5}>
              <Box display="flex">
                <Box flexGrow="1" mr={1} textAlign="right">
                  <Search
                    onSearchChange={(query, scope) => onSearchChange(query, scope, selection)}
                    search={search}
                    hasSelected={selection.ids.length > 0}
                    sessionSequenceType={sessionSequenceType}
                    alignedSequences={alignedSequences}
                  />
                </Box>
                <Box>
                  {selectedViewOption && (
                    <ToggleButton<SequenceAnalyserTableViews>
                      selectedOption={selectedViewOption}
                      options={viewOptions(maskingResidue)}
                      onChange={(viewOption) => onViewChange(viewOption.id)}
                      variant="dropdown"
                      itemsAnchor="right"
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {!SHOW_PRINT_VIEW && alignedSequences.length > 0 && (
        <SequenceTableRuler
          totalLength={sequenceAlignLength(longestSequence(alignedSequences))}
          zoomLevel={zoomLevel}
          sidebarPixelWidth={sidebarPixelWidth}
        />
      )}

      {openEditSelectionRangeModal && (
        <EditSelectionRangeModal
          open={openEditSelectionRangeModal}
          onOpen={(value) => setOpenEditSelectionRangeModal(value)}
          selection={selection}
          alignedSequences={alignedSequences}
        />
      )}
    </Box>
  );
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  zoomLevel: state.sequenceAnalyser.zoomLevel,
});

export default connect(mapStateToProps)(TableActionsHeader);
